/**
 * PRD.244 — shared helpers for item-level payment allocation.
 *
 * `itemChargeCents` mirrors `rowTotal` in consultation-data (subtotal −
 * discount, honoring fixed/percentage clamping, + tax) but operates on the
 * persisted `InvoiceItemRow` shape so both the Record Payment dialog and the
 * read-only item payment-status panel compute charge the same way.
 */

import type { InvoiceItemRow } from '@helios/data-access';
import {
  addMoney,
  multiplyMoney,
  parseAmountInput,
  percentageOf,
  phpFromMajor,
  subtractMoney,
  toCents,
  type Money,
} from '@helios/domain';

/** Charge (in cents) for one invoice item. */
export function itemChargeCents(row: InvoiceItemRow): number {
  const qty = row.qty ?? 1;
  const subtotal = multiplyMoney(phpFromMajor(row.unit_price ?? 0), qty);
  const rawDisc = Number.isFinite(row.discount) ? (row.discount ?? 0) : 0;
  let discount: Money;
  if (row.discount_type === 'percentage') {
    discount = percentageOf(subtotal, Math.max(0, Math.min(100, rawDisc)));
  } else {
    const requested = phpFromMajor(rawDisc);
    discount =
      requested.getAmount() > subtotal.getAmount()
        ? subtotal
        : requested.isNegative()
          ? phpFromMajor(0)
          : requested;
  }
  const base = subtractMoney(subtotal, discount);
  return toCents(addMoney(base, percentageOf(base, row.tax_rate ?? 0)));
}

/** Parse an allocation input string to integer cents (0 when empty/invalid). */
export function inputToCents(v: string | undefined): number {
  const p = v ? parseAmountInput(v) : null;
  return p ? toCents(p) : 0;
}
